import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSite } from '../queryKeys';

import { createSiteViaCSV } from './createSiteViaCSV';
import { type CreateSiteViaCSVMutationOptions } from './types';

/**
 * Custom hook for creating sites from a CSV file.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/import_sites_from_csv_site_import_csv_post
 */
export const useMutationCreateSiteViaCSV = (
  options?: CreateSiteViaCSVMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => createSiteViaCSV(file),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSite.getAll({}),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
